import { Skeleton } from '@shared/components/ui/shadcn';
import { cn } from '@shared/utils/utils';

/**
 * Componente ProductDataSkeleton - Muestra skeletons para los bloques del formulario de producto
 * 
 * Este componente replica la estructura de ProductData pero con skeletons
 * para mostrar un estado de carga visual mientras se cargan los datos.
 */
export const ProductDataSkeleton = () => {
  return (
    <form className="w-full">
      {/* Resumen Block Skeleton - Bloque de resumen con skeleton */}
      <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
        {/* Header Section - Sección de encabezado (izquierda) */}
        <div className="col-span-1 md:col-span-2">
          <Skeleton className="h-6 w-32 rounded-full mb-2 bg-hover-press" />
          <Skeleton className="h-4 w-48 rounded-full bg-hover-press" />
        </div>
        {/* Form Fields Section - Sección de campos del formulario (derecha) */}
        <div className="col-span-1 md:col-span-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nombre del producto - Product name */}
            <div className="flex flex-col gap-1 md:col-span-2">
              <Skeleton className="h-4 w-24 rounded-full mb-2 bg-hover-press" />
              <Skeleton className="h-10 w-full bg-hover-press" />
            </div>
            {/* Código - Code */}
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-16 rounded-full mb-2 bg-hover-press" />
              <div className="flex">
                <Skeleton className="h-10 flex-1 rounded-r-none bg-hover-press" />
                <Skeleton className="h-10 w-24 ml-[-3px] rounded-l-none bg-hover-press" />
              </div>
            </div>
            {/* Categoría - Category */}
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-20 rounded-full mb-2 bg-hover-press" />
              <Skeleton className="h-10 w-full bg-hover-press" />
            </div>
            {/* Descripción - Description */}
            <div className="flex flex-col gap-1 md:col-span-2">
              <Skeleton className="h-4 w-28 rounded-full mb-2 bg-hover-press" />
              <Skeleton className="h-24 w-full bg-hover-press" />
            </div>
          </div>
        </div>
      </div>

      {/* Imagen Block Skeleton - Bloque de imagen con skeleton */}
      <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
        {/* Header Section - Sección de encabezado (izquierda) */}
        <div className="col-span-1 md:col-span-2">
          <Skeleton className="h-6 w-32 rounded-full mb-2 bg-hover-press" />
          <Skeleton className="h-4 w-48 rounded-full bg-hover-press" />
        </div>
        {/* Image Upload Section - Sección de carga de imagen (derecha) */}
        <div className="col-span-1 md:col-span-4 flex flex-col md:flex-row md:items-center gap-5 !mt-0">
          {/* Image Preview - Vista previa de imagen */}
          <div className="relative flex items-center justify-center w-24 h-24 mx-auto md:mx-0">
            <Skeleton className="w-24 h-24 rounded-full bg-hover-press" />
            <Skeleton className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-hover-press" />
          </div>
          {/* Image Help Text - Texto de ayuda de imagen */}
          <div className="text-center md:text-left space-y-2">
            <Skeleton className="h-4 w-48 rounded-full mx-auto md:mx-0 bg-hover-press" />
            <Skeleton className="h-3 w-36 rounded-full mx-auto md:mx-0 bg-hover-press" />
          </div>
        </div>
      </div>

      {/* Información adicional Block Skeleton - Bloque de información adicional con skeleton */}
      <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4">
        {/* Header Section - Sección de encabezado (izquierda) */}
        <div className="col-span-1 md:col-span-2">
          <Skeleton className="h-6 w-40 rounded-full mb-2 bg-hover-press" />
          <Skeleton className="h-4 w-52 rounded-full bg-hover-press" />
        </div>
        {/* Additional Info Fields Section - Sección de campos de información adicional (derecha) */}
        <div className="col-span-1 md:col-span-4 !mt-0">
          {/* Coste y Peso - Cost and Weight */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mb-4">
            {/* Coste - Cost */}
            <div className="flex flex-col gap-1 items-start pt-1">
              <Skeleton className="h-4 w-16 rounded-full mb-2 bg-hover-press" />
              <Skeleton className="h-10 w-full bg-hover-press" />
            </div>
            {/* Peso - Weight */}
            <div className="flex flex-col gap-1 items-start">
              <div className="flex items-center justify-between gap-2 w-full mb-2">
                <Skeleton className="h-4 w-12 rounded-full flex-1 bg-hover-press" />
                <Skeleton className="h-4 w-8 rounded-full bg-hover-press" />
              </div>
              <Skeleton className="h-10 w-full bg-hover-press" />
            </div>
          </div>
          {/* Dimensiones - Dimensions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            {/* Ancho - Width */}
            <div className="flex flex-col gap-1 items-start">
              <Skeleton className="h-4 w-16 rounded-full mb-2 bg-hover-press" />
              <Skeleton className="h-10 w-full bg-hover-press" />
            </div>
            {/* Alto - Height */}
            <div className="flex flex-col gap-1 items-start">
              <Skeleton className="h-4 w-12 mb-2 bg-hover-press" />
              <Skeleton className="h-10 w-full bg-hover-press" />
            </div>
            {/* Largo - Length */}
            <div className="flex flex-col gap-1 items-start">
              <div className="flex items-center justify-between gap-2 w-full mb-2">
                <Skeleton className="h-4 w-14 rounded-full flex-1 bg-hover-press" />
                <Skeleton className="h-4 w-8 rounded-full bg-hover-press" />
              </div>
              <Skeleton className="h-10 w-full bg-hover-press" />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
